<!--
  eslint-disable vuejs-accessibility/click-events-have-key-events
  eslint-disable vuejs-accessibility/no-static-element-interactions  | TODO fix lint errors https://github.com/vue-a11y/eslint-plugin-vuejs-accessibility/tree/main/docs
-->
<template>
  <div
    class="z-modal fixed bottom-0 left-0 right-0 top-0 overflow-scroll bg-black bg-opacity-70"
    data-test="modal-wrapper"
    @click.self="autoclose ? close() : $emit('bg-click', modalId)"
  >
    <div
      ref="modalBody"
      role="dialog"
      aria-modal="true"
      tabindex="-1"
      :aria-label="ariaLabel"
      class="max-h-full-8 border-primary-200 mx-auto my-4 flex w-full max-w-2xl flex-col gap-y-6 overflow-hidden rounded-3xl border bg-white py-6 shadow-xl"
    >
      <AccessibilityAlert :region-id="`${modalId}-status-region`" />
      <header class="border-base mx-6 flex-none border-b pb-4">
        <slot name="header-icon" />
        <span
          :id="titleId"
          :role="titleIsLive ? 'status' : undefined"
          data-test="modal-title"
          class="text-primary-400 text-xs font-bold uppercase tracking-wide"
        >
          {{ modalTitle }}
        </span>
      </header>
      <div class="min-h-32 my-4 flex flex-grow flex-col gap-y-6 overflow-y-scroll px-6">
        <div v-if="modalPrompt" class="flex flex-col">
          <h2 :id="promptId" data-test="modal-prompt" class="text-primary-600 my-0 text-2xl font-bold">
            {{ modalPrompt }}
          </h2>
          <span v-if="modalDescription" :id="descriptionId" data-test="modal-description" class="text-muted text-sm">
            {{ modalDescription }}
          </span>
        </div>
        <slot v-bind="{ modalId, close }" />
      </div>
      <footer
        v-if="isSlotUsed($slots, 'footer')"
        :class="[
          'border-base mx-6 flex flex-none content-center justify-center gap-x-2.5 border-t pt-4',
          footerClasses,
        ]"
      >
        <slot name="footer" v-bind="{ close }" />
      </footer>
    </div>
  </div>
</template>

<script>
import { closeModal } from 'vue_features/shared/composables/use_global_modals'
import { computed, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import useFocusTrap from 'vue_features/shared/composables/use_focus_trap'
import { isSlotUsed } from 'vue_features/shared/helpers/slot_helpers'
import { common } from 'vue_features/shared/i18n'
import { namespaceLocaleObj } from 'vue_features/shared/helpers/i18n_helper'
import AccessibilityAlert from 'vue_features/shared/components/accessibility/AccessibilityAlert'

export default {
  name: 'Modal',
  components: { AccessibilityAlert },
  props: {
    modalId: { type: String, required: true },
    modalTitle: { type: String, default: '' },
    modalPrompt: { type: String, default: '' },
    modalDescription: { type: String, default: '' },
    autoclose: { type: Boolean, default: true },
    footerClasses: { type: [String, Array, Object], default: '' },
  },
  setup(props) {
    const modalBody = ref()
    useFocusTrap(modalBody, true, {
      escapeDeactivates: false,
      // A backdrop click doesn't necessarily close the dialog (autoclose is opt-in, and some
      // consumers ignore bg-click entirely), so the trap must not release focus on its own —
      // otherwise focus can escape into content aria-modal has already hidden from AT.
      // allowOutsideClick must go with this: without it, focus-trap itself swallows the
      // backdrop click (preventDefault + stopImmediatePropagation) before it ever reaches the
      // @click.self handler above that actually implements autoclose/bg-click.
      clickOutsideDeactivates: false,
      allowOutsideClick: true,
      initialFocus: () => modalBody.value,
    })
    const { t } = useI18n({ messages: namespaceLocaleObj(common, 'Modal', { only: ['dialog'] }) })

    // Starts false so the title isn't a live region on the dialog's very first paint - a live
    // region that's inserted already holding content is a known trigger for screen readers
    // announcing it as a competing, out-of-order interruption to the dialog's own opening
    // announcement. Flips true a tick after mount so a later step change (e.g. "Step 2 of 2")
    // still gets announced, without racing the initial open.
    const titleIsLive = ref(false)
    onMounted(() => {
      setTimeout(() => {
        titleIsLive.value = true
      }, 0)
    })

    const titleId = computed(() => `${props.modalId}-title`)
    const promptId = computed(() => `${props.modalId}-prompt`)
    const descriptionId = computed(() => `${props.modalId}-description`)
    // A plain aria-label string, not aria-labelledby/aria-describedby pointing at the title/
    // prompt/description spans: initial focus lands on this element itself (see initialFocus
    // above), and VoiceOver/Safari has been observed to intermittently botch or drop the name
    // (and, less often but still real, the description) when a just-inserted, just-focused
    // element's accessible text has to be cross-referenced from sibling nodes rather than read
    // directly off the element. Folding the description in here too, rather than leaving it on
    // aria-describedby, removes that last cross-reference.
    const ariaLabel = computed(() =>
      props.modalPrompt
        ? [props.modalTitle, props.modalPrompt, props.modalDescription].filter(Boolean).join(' ')
        : props.modalTitle || t('Modal.dialog')
    )

    return {
      close: () => closeModal(props.modalId),
      modalBody,
      isSlotUsed,
      titleIsLive,
      titleId,
      promptId,
      descriptionId,
      ariaLabel,
    }
  },
}
</script>
